import React, {Component} from "react"
import {MutationFn} from "react-apollo"

import {Poll} from "@irati/types"
import {ApolloState} from "@irati/utils/services/apollo/apolloStateInterface"
import {validatedScreen} from "@irati/utils/validation"
import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import {CompanyValuesSelect} from "@irati-web/fields/companyValues"
import getId from "@irati/utils/id"
import {Title, Desc, Teams, EndDate, Share} from "@irati-web/fields/poll"
import Trophy from "@irati-web/fields/trophy"
import tracking from "@irati-tracking/recognition/poll"
import {EVERYONE_OPTION, SPECIFIC_TEAMS_OPTION, SPECIFIC_TEAMS} from "@irati/recognition/constants/share"
import {H3} from "@irati/ui/src/Typography/Heading"
import Flex from "@irati/ui/src/Flex"

import CTA from "../shared/CTA"
import {DATE_PICKER, INSPIRE} from "./Popup"
import DateWarning from "./shared/DateWarning"
import InspireMe from "./shared/InspireMe"

import {HeaderContainer, StyledForm, StyledHr} from "./styled/Create"

interface Props extends InjectIntl {
  poll: Poll
  apolloState: ApolloState
  onSubmit: MutationFn
  onChange: (poll: Poll, property: string, value: string) => void
  onSharingOptionChange: (share: string) => void
  selectScreen: (modalToShow: string | null) => void
  share: string
  focusReward?: boolean
  id?: string
}

interface State {
  submit: Promise<any> | null
  trophyValidationError: boolean
}

class CreatePoll extends Component<Props, State> {
  static defaultProps: Pick<Props, "focusReward" | "id"> = {
    focusReward: false,
    id: "createPoll"
  }

  state: State = {
    submit: null,
    trophyValidationError: false
  }

  handleNext = () => {
    const {onSubmit, poll} = this.props
    const submit = onSubmit()

    if (!poll.trophy) {
      this.setState({trophyValidationError: true})
    }

    this.setState({submit})
    return submit
  }

  handleCancel = () => {
    tracking.cancelPoll()
    this.props.selectScreen(null)
  }

  handleTrophySelect = (trophy: string) => {
    this.props.onChange(this.props.poll, "trophy", trophy)
  }

  handleCustomDate = () => {
    this.props.selectScreen(DATE_PICKER)
  }

  handleInspire = () => {
    tracking.viewInspirations()
    this.props.selectScreen(INSPIRE)
  }

  handleShareToggle = (currentValue, key, {value}) => {
    this.props.onSharingOptionChange(value)
  }

  render() {
    const {poll, share, onChange, apolloState, formatMessage, focusReward} = this.props
    const {submit, trophyValidationError} = this.state

    return (
      <>
        <HeaderContainer>
          <Flex alignItems="center">
            <H3 marginRight={1}>
              <FormattedMessage id="recognition.popup.createPoll.create.header" />
            </H3>
            <InspireMe onClick={this.handleInspire} />
          </Flex>
        </HeaderContainer>
        <StyledHr />
        <StyledForm>
          <Title autoFocus={!focusReward} poll={poll} onChange={onChange} />
          <Trophy
            componentToHideOnExpand={() => <Desc poll={poll} onChange={onChange} />}
            item={poll}
            onChange={this.handleTrophySelect}
          />
          <CompanyValuesSelect item={poll} onChange={onChange} />
          <EndDate poll={poll} onChange={onChange} onCustomDate={this.handleCustomDate} />
          <DateWarning poll={poll} />
          <Share
            options={[EVERYONE_OPTION(formatMessage), SPECIFIC_TEAMS_OPTION(formatMessage)]}
            poll={{share}}
            onChange={this.handleShareToggle}
          />
          {share === SPECIFIC_TEAMS && <Teams poll={poll} onChange={onChange} />}
        </StyledForm>
        <CTA
          id={getId("submitButton", this.props)}
          onNext={this.handleNext}
          onCancel={this.handleCancel}
          load={submit}
          apolloState={apolloState}
          trophyValidationError={trophyValidationError}
        />
      </>
    )
  }
}

export default injectIntl(validatedScreen(CreatePoll, "CreatePoll"))
